import { Injectable, signal } from '@angular/core';

export interface Toast {
  id: number;
  type: 'success' | 'error' | 'info' | 'warning';
  message: string;
  protocol?: string;
}

@Injectable({
  providedIn: 'root'
})
export class ToastService {
  toasts = signal<Toast[]>([]);
  private counter = 0;

  showError(error: any, context: string = 'Erro na operação') {
    console.error(`[${context}]`, error);

    let userMessage = 'Ocorreu um erro inesperado. Tente novamente.';
    let details = '';

    // 1. Supabase / Postgres Errors
    if (error?.code) {
      switch (error.code) {
        case '23505': // Unique Violation
          if (error.details?.includes('Key (cpf)')) {
            userMessage = 'Este CPF já está cadastrado no sistema.';
          } else if (error.details?.includes('Key (sus_card)')) {
            userMessage = 'Este Cartão SUS já está cadastrado.';
          } else {
            userMessage = 'Registro duplicado detectado.';
          }
          break;
        case '23503': // Foreign Key Violation
          userMessage = 'Não é possível realizar esta ação pois existem registros dependentes.';
          break;
        case '23502': // Not Null Violation
          const column = error.details?.match(/column "(.+?)"/)?.[1] || 'obrigatório';
          userMessage = `O campo "${column}" é obrigatório e não foi preenchido.`;
          break;
        case '22007': // Invalid Date Syntax
          userMessage = 'Formato de data inválido. Verifique os campos de data.';
          break;
        case '22P02': // Invalid Text Representation
          userMessage = 'Formato de dados inválido (ex: texto em campo numérico).';
          break;
        case '42P01': // Undefined Table
          userMessage = 'Erro interno: Tabela não encontrada. Contate o suporte.';
          break;
        case 'PGRST116': // JSON object not found (usually logic error or missing record)
          userMessage = 'Recurso não encontrado ou erro de comunicação.';
          break;
        case 'PGRST301': // JWT expired
          userMessage = 'Sua sessão expirou. Por favor, faça login novamente.';
          break;
        default:
          userMessage = `Erro no banco de dados. Código: ${error.code}`;
      }
      details = error.details || error.message || '';
    }
    // 2. Generic JS Errors
    else if (error?.message) {
      const msg = error.message.toLowerCase();
      if (msg.includes('failed to fetch') || msg.includes('network error')) {
        userMessage = 'Falha na conexão com o servidor. Verifique sua internet.';
      } else if (msg.includes('not found')) {
        userMessage = 'Registro não encontrado.';
      } else if (msg.includes('unauthorized') || msg.includes('401')) {
        userMessage = 'Acesso não autorizado ou sessão expirada.';
      } else if (msg.includes('invalid credentials')) {
        userMessage = 'Login ou senha incorreto.';
      } else if (msg.includes('cannot read') && msg.includes('operator')) {
        userMessage = 'Erro ao carregar dados do usuário. Faça login novamente.';
      } else if (msg.includes('user not found')) {
        userMessage = 'Usuário não localizado no sistema.';
      } else {
        // Se for uma mensagem curta em inglês conhecida, traduzimos, caso contrário mantemos
        userMessage = error.message;
      }
    }

    // Append context to message for clarity
    const finalMessage = `${context}: ${userMessage}`;

    // Show Toast
    this.show(finalMessage, 'error');
  }

  showSuccess(message: string) {
    this.show(message, 'success');
  }

  showWarning(message: string) {
    this.show(message, 'warning');
  }

  success(message: string) {
    this.showSuccess(message);
  }

  info(message: string) {
    this.show(message, 'info');
  }

  warning(message: string) {
    this.showWarning(message);
  }

  error(message: string | any, context?: string) {
    if (typeof message === 'string' && !context) {
      this.show(message, 'error');
    } else {
      this.showError(message, context);
    }
  }

  show(message: string, type: 'success' | 'error' | 'info' | 'warning' = 'info', protocol?: string) {
    const id = this.counter++;
    this.toasts.update(current => [...current, { id, message, type, protocol }]);

    // Auto remove after 5 seconds (increased slightly to allow reading protocol)
    setTimeout(() => {
      this.remove(id);
    }, 6000);
  }

  remove(id: number) {
    this.toasts.update(current => current.filter(t => t.id !== id));
  }
}

